/**
 * AuthProvider — the gate between unauthenticated UI and the real app.
 *
 * Lifecycle (matching V1_ARCHITECTURE.md Section 5):
 *   1. On mount: check we're inside Telegram. If not, set `status='not_in_tg'`.
 *   2. POST initData to /auth/telegram → tg_session cookie set server-side.
 *   3. Wait for the user to connect a wallet via TonConnect (which we also
 *      drive — we listen on tonConnectUI.onStatusChange).
 *   4. When wallet connects with proof attached, POST it to /auth/wallet,
 *      get a JWT, store in CloudStorage, set status='ready'.
 *   5. If a JWT already exists in CloudStorage on boot, skip step 4 and
 *      verify it via /me. On 401, clear and restart from step 3.
 *
 * Status is exposed via context. Anything outside <AuthGate> is rendered
 * regardless; the gate decides what to show based on status.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useTonConnectUI } from "@tonconnect/ui-react";
import {
  ApiError,
  authDevSession,
  authProofNonce,
  authTelegram,
  authWallet,
  getMe,
  setApiJwt,
} from "../lib/api";
import { clearJwt, loadJwt, saveJwt } from "../lib/auth";
import {
  applyTheme,
  haptics,
  isInTelegram,
  tmaReady,
} from "../lib/telegram";
import { streamClient } from "../lib/stream";
import type { MeResponse } from "../types/api";

export type AuthStatus =
  | "booting" // checking storage, verifying initData
  | "not_in_tg" // running in a regular browser; show informational error
  | "needs_wallet" // initData verified; user must connect wallet
  | "verifying_wallet" // wallet connected, exchanging proof for JWT
  | "ready" // signed in, JWT cached, /me known
  | "error"; // unrecoverable; surface message

interface AuthContextValue {
  status: AuthStatus;
  me: MeResponse | null;
  errorMessage: string | null;
  /** Sign out: clear JWT, disconnect wallet, drop to needs_wallet. */
  signOut: () => Promise<void>;
  /** Dev-only: short-circuit to ready using DEV_SEED_WALLET. */
  devSignIn: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [status, setStatus] = useState<AuthStatus>("booting");
  const [me, setMe] = useState<MeResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tonConnectUI] = useTonConnectUI();

  /* ---------- Boot: theme + initData + (maybe) existing JWT ---------- */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      tmaReady();
      applyTheme();

      if (!isInTelegram()) {
        // Outside Telegram, the dev bypass is the only path to a tg_session.
        // But if we already have a JWT from a previous dev sign-in, it
        // outlives the tg_session (JWT 7d vs session 10min) — try it before
        // forcing the user back through the bypass flow.
        const stored = await loadJwt();
        if (stored !== null && stored.length > 0) {
          setApiJwt(stored);
          try {
            const meResp = await getMe();
            if (cancelled) return;
            setMe(meResp);
            streamClient.setJwt(stored);
            setStatus("ready");
            return;
          } catch {
            await clearJwt();
            setApiJwt(null);
          }
        }
        if (!cancelled) setStatus("not_in_tg");
        return;
      }

      try {
        // Step 1: TG session cookie. Idempotent.
        await authTelegram();
      } catch (err) {
        if (!cancelled) {
          setStatus("error");
          setErrorMessage(
            err instanceof Error
              ? `Telegram verification failed: ${err.message}`
              : "Telegram verification failed.",
          );
        }
        return;
      }

      // Step 2: existing JWT?
      const stored = await loadJwt();
      if (stored !== null && stored.length > 0) {
        setApiJwt(stored);
        try {
          const meResp = await getMe();
          if (cancelled) return;
          setMe(meResp);
          streamClient.setJwt(stored);
          setStatus("ready");
          return;
        } catch (err) {
          // Stored JWT no longer valid; clear and ask for wallet again.
          await clearJwt();
          setApiJwt(null);
          if (err instanceof ApiError && err.status !== 401) {
            // network or 5xx — surface but still let them retry connect.
            if (!cancelled) setErrorMessage(err.message);
          }
        }
      }

      if (!cancelled) {
        setStatus("needs_wallet");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  /* --------------- Listen on TonConnect status changes ---------------- */
  useEffect(() => {
    // Refresh tonProof nonce on every fresh connect attempt so we don't burn
    // a nonce on a long-idle session. We request one as soon as we're in
    // needs_wallet status; the wallet UI may sit open for minutes.
    if (status !== "needs_wallet") return;
    let cancelled = false;
    // Eagerly mark params 'loading' so the SDK knows we're going to set them.
    tonConnectUI.setConnectRequestParameters({ state: "loading" });
    window.dispatchEvent(new CustomEvent("tg-debug", { detail: "params: loading" }));
    (async () => {
      try {
        const { nonce } = await authProofNonce();
        if (cancelled) return;
        tonConnectUI.setConnectRequestParameters({
          state: "ready",
          value: { tonProof: nonce },
        });
        window.dispatchEvent(
          new CustomEvent("tg-debug", { detail: `params: ready (nonce ${nonce.length}c)` }),
        );
      } catch (err) {
        if (cancelled) return;
        // Without a nonce we can't include tonProof; passing null clears the
        // loading state so the modal can proceed without a proof binding.
        tonConnectUI.setConnectRequestParameters(null);
        window.dispatchEvent(
          new CustomEvent("tg-debug", {
            detail: `params: cleared (no nonce; ${err instanceof Error ? err.message : "?"})`,
          }),
        );
        setErrorMessage(
          err instanceof Error
            ? `Could not request proof nonce: ${err.message}`
            : "Could not request proof nonce.",
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [status, tonConnectUI]);

  useEffect(() => {
    const unsub = tonConnectUI.onStatusChange(async (walletInfo) => {
      if (walletInfo === null) {
        // Disconnected. If we were ready, stay where we are — the JWT outlives
        // the wallet connection. Only matters if the user explicitly signs out.
        return;
      }
      const account = walletInfo.account;
      const proof = walletInfo.connectItems?.tonProof;
      // Guard: tonProof can be { proof } | { error }
      if (
        !proof ||
        !("proof" in proof) ||
        proof.proof === undefined ||
        proof.proof === null
      ) {
        // Wallet connected without proof (e.g., re-open of an existing session
        // from TonConnect storage). If we already have a JWT it's fine; if not,
        // we cannot upgrade and must ask the user to fully reconnect.
        return;
      }
      const p = proof.proof;
      setStatus("verifying_wallet");
      haptics.impact("light");
      try {
        const { jwt, user } = await authWallet({
          address: account.address,
          network: account.chain,
          public_key: account.publicKey ?? "",
          proof: {
            timestamp: p.timestamp,
            domain: {
              length_bytes: p.domain.lengthBytes,
              value: p.domain.value,
            },
            payload: p.payload,
            signature: p.signature,
          },
        });
        setApiJwt(jwt);
        await saveJwt(jwt);
        setMe(user);
        streamClient.setJwt(jwt);
        setStatus("ready");
        setErrorMessage(null);
        haptics.notify("success");
      } catch (err) {
        haptics.notify("error");
        setStatus("needs_wallet");
        setErrorMessage(
          err instanceof Error
            ? `Wallet verification failed: ${err.message}`
            : "Wallet verification failed.",
        );
      }
    });
    return () => {
      unsub();
    };
  }, [tonConnectUI]);

  /* ----- React to API 401s — clear JWT, restart wallet flow ----- */
  useEffect(() => {
    const onInvalid = (): void => {
      void (async () => {
        await clearJwt();
        setApiJwt(null);
        streamClient.setJwt(null);
        setMe(null);
        setStatus("needs_wallet");
      })();
    };
    window.addEventListener("auth:invalid", onInvalid);
    return () => window.removeEventListener("auth:invalid", onInvalid);
  }, []);

  /* ------------- Apply theme on TG theme_changed events ------------- */
  useEffect(() => {
    const w = window as unknown as {
      Telegram?: {
        WebApp?: { onEvent?: (n: string, cb: () => void) => void; offEvent?: (n: string, cb: () => void) => void };
      };
    };
    const wa = w.Telegram?.WebApp;
    if (!wa?.onEvent || !wa?.offEvent) return;
    const handler = (): void => applyTheme();
    wa.onEvent("themeChanged", handler);
    return () => {
      wa.offEvent?.("themeChanged", handler);
    };
  }, []);

  const devSignIn = useCallback(async () => {
    setErrorMessage(null);
    try {
      // Issue a synthetic tg_session, then let the normal TonConnect flow run.
      // The wallet the user picks determines the JWT — no DEV_SEED_WALLET.
      await authDevSession();
      setStatus("needs_wallet");
    } catch (err) {
      setStatus("not_in_tg");
      setErrorMessage(
        err instanceof Error
          ? `Dev session failed: ${err.message}`
          : "Dev session failed.",
      );
    }
  }, []);

  const signOut = useCallback(async () => {
    await clearJwt();
    setApiJwt(null);
    streamClient.setJwt(null);
    try {
      await tonConnectUI.disconnect();
    } catch {
      /* already disconnected */
    }
    setMe(null);
    setStatus("needs_wallet");
  }, [tonConnectUI]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, me, errorMessage, signOut, devSignIn }),
    [status, me, errorMessage, signOut, devSignIn],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const v = useContext(AuthContext);
  if (v === null) throw new Error("useAuth() used outside <AuthProvider>");
  return v;
}
